import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { ArrowLeft, MapPin, Clock, Route as RouteIcon, Share2, Sparkles, Loader2, Pencil, Trash2, Download, Volume2, Mic2, Globe2, Check } from "lucide-react";
import { useLang } from "../LangContext";
import { useAuth, adminAxios } from "../AuthContext";
import { TravelCostCard } from "./ItinerariPage";
import { StopMediaGallery, Panorama360Viewer } from "../components/StopMedia";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayColor = (n) => ["#059669", "#d97706", "#7c3aed", "#e11d48", "#0284c7", "#65a30d", "#f59e0b"][n % 7];

const LANG_FLAGS = { it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷", es: "🇪🇸", de: "🇩🇪" };
const LANG_LABELS = { it: "Italiano", en: "English", fr: "Français", es: "Español", de: "Deutsch" };

const stopIcon = (n, day) => new L.DivIcon({
    className: "itin-stop-pin",
    html: `<div style="background:${dayColor(day - 1)};color:white;width:28px;height:28px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;font-family:sans-serif;">${n}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
});

export default function ItinerarioDetailPage() {
    const { id } = useParams();
    const { lang } = useLang();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [itin, setItin] = useState(null);
    const [err, setErr] = useState("");
    const [shared, setShared] = useState(false);
    const [busy, setBusy] = useState(false);
    const [panorama, setPanorama] = useState(null);
    // Audio: { [lang]: { "day-stop": url } }
    const [audioByLang, setAudioByLang] = useState({});
    const [audioBusy, setAudioBusy] = useState(false);
    // Which languages the admin wants to generate/consult (multi-select)
    const [selectedLangs, setSelectedLangs] = useState(["it"]);
    // Which language players are visible on each stop (defaults to itinerary default lang)
    const [activeLang, setActiveLang] = useState("it");

    const loadAudioStatus = async () => {
        try {
            const { data } = await axios.get(`${API}/itineraries/${id}/audio/status`);
            const byLang = {};
            (data.audios || []).forEach((a) => {
                Object.entries(a.langs || {}).forEach(([lg, url]) => {
                    if (!byLang[lg]) byLang[lg] = {};
                    byLang[lg][`${a.day}-${a.stop}`] = url || "";
                });
            });
            setAudioByLang(byLang);
            // On first load align activeLang & selectedLangs with itinerary default
            if (data.default_lang) {
                setActiveLang((prev) => prev || data.default_lang);
                setSelectedLangs((prev) => (prev.length ? prev : [data.default_lang]));
            }
        } catch (_) { /* ignore */ }
    };
    useEffect(() => {
        if (itin) {
            const dl = itin.filters?.language || "it";
            setActiveLang(dl);
            setSelectedLangs([dl]);
            loadAudioStatus();
        }
    }, [itin]);

    const toggleLang = (code) => {
        setSelectedLangs((prev) => prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]);
    };

    const generateAllAudio = async () => {
        if (!isAdmin || !canManage) return;
        if (!selectedLangs.length) {
            window.alert("Seleziona almeno una lingua per l'audio guida.");
            return;
        }
        const flagList = selectedLangs.map((l) => LANG_FLAGS[l] || l).join(" ");
        if (!window.confirm(`Generare l'audio guida per TUTTE le tappe nelle lingue ${flagList}?\nVerranno consumati crediti AI (traduzione + TTS).`)) return;
        setAudioBusy(true);
        try {
            await adminAxios().post(`/itineraries/${itin.id}/audio/generate`, { items: [], langs: selectedLangs });
            await loadAudioStatus();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Errore generazione audio");
        } finally { setAudioBusy(false); }
    };

    const generateOneAudio = async (day, stop) => {
        if (!selectedLangs.length) {
            window.alert("Seleziona almeno una lingua per l'audio guida.");
            return;
        }
        setAudioBusy(true);
        try {
            await adminAxios().post(`/itineraries/${itin.id}/audio/generate`, { items: [{ day, stop }], langs: selectedLangs });
            await loadAudioStatus();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Errore generazione audio");
        } finally { setAudioBusy(false); }
    };

    const isAdmin = !!user && (user.role === "super_admin" || user.role === "borgo_admin" || user.role === "admin");
    const canManage = itin && (user?.role === "super_admin" || itin.tenant_id === user?.tenant_id);

    useEffect(() => {
        axios.get(`${API}/itineraries/${id}`)
            .then((r) => setItin(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Errore caricamento"));
    }, [id]);

    const handleDelete = async () => {
        if (!window.confirm(`Eliminare definitivamente "${itin.title}"?`)) return;
        setBusy(true);
        try {
            await adminAxios().delete(`/itineraries/${itin.id}`);
            navigate(`/itinerari?borgo=${itin.borgo_slug}`);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore eliminazione");
            setBusy(false);
        }
    };

    if (err) return <div className="pt-32 min-h-screen text-center text-red-600" data-testid="itin-detail-error">{err}</div>;
    if (!itin) return <div className="pt-32 min-h-screen text-center"><Loader2 size={26} className="animate-spin inline text-emerald-600" /></div>;

    const allStops = (itin.days || []).flatMap((d) => (d.stops || []).map((s, idx) => ({ ...s, day: d.day, idxInDay: idx + 1 })))
        .filter((s) => typeof s.lat === "number" && typeof s.lng === "number");
    const center = allStops.length > 0 ? [
        allStops.reduce((a, s) => a + s.lat, 0) / allStops.length,
        allStops.reduce((a, s) => a + s.lng, 0) / allStops.length,
    ] : [40.0833, 8.9167];

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: itin.title, url });
            } else {
                await navigator.clipboard.writeText(url);
                setShared(true); setTimeout(() => setShared(false), 2500);
            }
        } catch { /* user cancelled */ }
    };

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="pt-24 pb-16 px-6 lg:px-10">
                <div className="max-w-6xl mx-auto">
                    <Link to={`/itinerari?borgo=${itin.borgo_slug}`} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-emerald-700 mb-4" data-testid="itin-back-list">
                        <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : lang === "es" ? "Volver" : lang === "de" ? "Zurück" : lang === "en" ? "Back" : "Indietro"}
                    </Link>

                    <div className="bg-white rounded-3xl border border-stone-200 p-6 md:p-8 shadow-sm">
                        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                            <div>
                                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">
                                    <Sparkles size={13} /> AI Itinerary · {itin.filters?.days || 1} {(itin.filters?.days || 1) === 1 ? "giorno" : "giorni"} · {itin.filters?.mobility}
                                </div>
                                <h1 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900" data-testid="itin-detail-title">{itin.title}</h1>
                                {itin.subtitle && <p className="mt-2 text-stone-600 max-w-2xl">{itin.subtitle}</p>}
                                <div className="flex flex-wrap gap-1 mt-3">
                                    {(itin.filters?.interests || []).map((iv) => (
                                        <span key={iv} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{iv}</span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {isAdmin && canManage && (
                                    <>
                                        <Link
                                            to={`/itinerari?borgo=${itin.borgo_slug}&edit=${itin.id}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold"
                                            data-testid="itin-detail-edit"
                                        >
                                            <Pencil size={12} /> Modifica
                                        </Link>
                                        <button
                                            onClick={handleDelete}
                                            disabled={busy}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold disabled:opacity-50"
                                            data-testid="itin-detail-delete"
                                        >
                                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Elimina
                                        </button>
                                    </>
                                )}
                                <a
                                    href={`${API}/itineraries/${itin.id}/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-800 hover:bg-stone-900 text-white text-xs font-semibold"
                                    data-testid="itin-detail-pdf"
                                >
                                    <Download size={13} /> PDF
                                </a>
                                <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="itin-share">
                                    <Share2 size={13} /> {shared ? "✔ Copiato!" : "Condividi"}
                                </button>
                            </div>
                        </div>

                        {/* AUDIO GUIDE — multilingual selector (admin only) */}
                        {isAdmin && canManage && (
                            <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 md:p-5 mb-5" data-testid="itin-audio-panel">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-indigo-700 mb-1">
                                            <Mic2 size={12} /> Audio guida multilingua
                                        </div>
                                        <p className="text-xs text-stone-600 max-w-lg">
                                            Seleziona le lingue in cui vuoi generare l&apos;audio guida. Il testo di ogni tappa viene tradotto automaticamente e trasformato in audio (voci OpenAI).
                                        </p>
                                    </div>
                                    <button
                                        onClick={generateAllAudio}
                                        disabled={audioBusy || !selectedLangs.length}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold"
                                        data-testid="itin-detail-audio-generate-all"
                                        title="Genera audio per tutte le tappe nelle lingue selezionate"
                                    >
                                        {audioBusy ? <Loader2 size={12} className="animate-spin" /> : <Mic2 size={12} />} Genera per tutte le tappe
                                    </button>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="itin-audio-lang-picker">
                                    {["it", "en", "fr", "es", "de"].map((code) => {
                                        const on = selectedLangs.includes(code);
                                        return (
                                            <button
                                                key={code}
                                                onClick={() => toggleLang(code)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-stone-700 border-stone-300 hover:border-indigo-400"}`}
                                                data-testid={`itin-audio-lang-${code}`}
                                            >
                                                <span className="text-sm leading-none">{LANG_FLAGS[code]}</span>
                                                {LANG_LABELS[code]}
                                                {on && <Check size={11} />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
                                    <span className="text-stone-500 flex items-center gap-1"><Globe2 size={11} /> Anteprima lingua nel player:</span>
                                    {["it", "en", "fr", "es", "de"].map((code) => (
                                        <button
                                            key={code}
                                            onClick={() => setActiveLang(code)}
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${activeLang === code ? "bg-stone-900 text-white" : "bg-white text-stone-600 border border-stone-200 hover:border-stone-400"}`}
                                            data-testid={`itin-audio-active-lang-${code}`}
                                        >
                                            {LANG_FLAGS[code]} {code.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Travel cost card (if present) */}
                        {itin.travel_cost && (
                            <TravelCostCard
                                travel={itin.travel_cost}
                                adults={itin.filters?.adults ?? 2}
                                k412={itin.filters?.children_4_12 ?? 0}
                                totalPpl={(itin.filters?.adults ?? 2) + (itin.filters?.children_4_12 ?? 0) + (itin.filters?.children_under_4 ?? 0)}
                            />
                        )}

                        {allStops.length > 0 && (
                            <div className="rounded-2xl overflow-hidden border border-stone-200 mb-6" style={{ height: 420 }}>
                                <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                                    {(itin.days || []).map((d) => (
                                        <Polyline
                                            key={d.day}
                                            positions={(d.stops || []).filter((s) => typeof s.lat === "number").map((s) => [s.lat, s.lng])}
                                            pathOptions={{ color: dayColor(d.day - 1), weight: 3, dashArray: "6 6", opacity: 0.7 }}
                                        />
                                    ))}
                                    {allStops.map((s, i) => (
                                        <Marker key={i} position={[s.lat, s.lng]} icon={stopIcon(s.idxInDay, s.day)}>
                                            <Popup>
                                                <div style={{ minWidth: 180 }}>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: dayColor(s.day - 1), textTransform: "uppercase", letterSpacing: 1 }}>Giorno {s.day}</div>
                                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.name}</div>
                                                    {s.description && <div style={{ fontSize: 11, color: "#57534e" }}>{s.description}</div>}
                                                </div>
                                            </Popup>
                                        </Marker>
                                    ))}
                                </MapContainer>
                            </div>
                        )}

                        <div className="space-y-5">
                            {(itin.days || []).map((d) => (
                                <div key={d.day} className="border-l-4 rounded-r-xl p-4 bg-stone-50" style={{ borderColor: dayColor(d.day - 1) }}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-serif text-xl font-semibold text-stone-900">Giorno {d.day} — {d.title}</div>
                                        <div className="text-xs text-stone-500 flex items-center gap-3">
                                            {d.total_km ? <span><RouteIcon size={11} className="inline mr-0.5" />{d.total_km} km</span> : null}
                                            {d.total_hours ? <span><Clock size={11} className="inline mr-0.5" />{d.total_hours}h</span> : null}
                                        </div>
                                    </div>
                                    {d.summary && <p className="text-sm text-stone-600 mb-3">{d.summary}</p>}
                                    <ol className="space-y-2">
                                        {(d.stops || []).map((s, i) => {
                                            const audioKey = `${d.day}-${i}`;
                                            const activeUrl = (audioByLang[activeLang] || {})[audioKey] || "";
                                            const langsWithAudio = ["it", "en", "fr", "es", "de"].filter(
                                                (lg) => (audioByLang[lg] || {})[audioKey]
                                            );
                                            return (
                                            <li key={i} className="flex gap-3 items-start bg-white p-3 rounded-xl">
                                                <div className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0" style={{ backgroundColor: dayColor(d.day - 1) }}>{i + 1}</div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <div className="font-semibold text-stone-900 text-sm">{s.name}</div>
                                                        {s.duration_min ? <span className="text-[10px] font-semibold text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">{s.duration_min} min</span> : null}
                                                        {s.kind ? <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full uppercase tracking-widest">{s.kind}</span> : null}
                                                        {/* Per-language status pills */}
                                                        {langsWithAudio.length > 0 && (
                                                            <div className="inline-flex items-center gap-1" data-testid={`itin-audio-langs-avail-${d.day}-${i}`}>
                                                                {langsWithAudio.map((lg) => (
                                                                    <button
                                                                        key={lg}
                                                                        onClick={() => setActiveLang(lg)}
                                                                        className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${activeLang === lg ? "bg-emerald-600 text-white border-emerald-600" : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"}`}
                                                                        title={`Audio disponibile: ${LANG_LABELS[lg]}`}
                                                                        data-testid={`itin-audio-langpill-${d.day}-${i}-${lg}`}
                                                                    >
                                                                        <span className="leading-none">{LANG_FLAGS[lg]}</span>
                                                                        {lg.toUpperCase()}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {isAdmin && canManage && (
                                                            <button
                                                                onClick={() => generateOneAudio(d.day, i)}
                                                                disabled={audioBusy || !selectedLangs.length}
                                                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-2 py-0.5 rounded-full disabled:opacity-50"
                                                                data-testid={`itin-audio-gen-${d.day}-${i}`}
                                                                title={`Genera audio in: ${selectedLangs.map((l) => LANG_FLAGS[l]).join(" ")}`}
                                                            >
                                                                {audioBusy ? <Loader2 size={11} className="animate-spin" /> : <Mic2 size={11} />}
                                                                {langsWithAudio.length ? "Aggiorna audio" : "Genera audio"}
                                                                <span className="ml-0.5">({selectedLangs.length})</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                    {activeUrl && (
                                                        <div className="mt-2 flex items-center gap-2">
                                                            <span className="text-[10px] font-semibold text-stone-500 flex items-center gap-1">
                                                                {LANG_FLAGS[activeLang]} {LANG_LABELS[activeLang]}
                                                            </span>
                                                            <audio
                                                                controls
                                                                preload="none"
                                                                className="flex-1 h-8"
                                                                src={`${process.env.REACT_APP_BACKEND_URL}${activeUrl}`}
                                                                data-testid={`itin-audio-player-${d.day}-${i}`}
                                                            />
                                                            <a
                                                                href={`${process.env.REACT_APP_BACKEND_URL}${activeUrl}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                                                data-testid={`itin-audio-play-${d.day}-${i}`}
                                                            >
                                                                <Volume2 size={11} /> MP3
                                                            </a>
                                                        </div>
                                                    )}
                                                    {s.address && <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-1"><MapPin size={10} /> {s.address}</div>}
                                                    {s.description && <div className="text-xs text-stone-600 mt-1 leading-relaxed">{s.description}</div>}
                                                    {s.tips && <div className="text-xs text-emerald-700 mt-1"><b>💡</b> {s.tips}</div>}
                                                    {s.travel_to_next && (
                                                        <div className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                                                            <RouteIcon size={12} className="mt-0.5 shrink-0" />
                                                            <span><b>→ Verso la tappa successiva:</b> {s.travel_to_next} {s.distance_to_next_km ? <span className="font-semibold">({s.distance_to_next_km} km)</span> : null}</span>
                                                        </div>
                                                    )}
                                                    {(s.cost_per_person_min != null || s.cost_per_person_max != null) ? (
                                                        <div className="mt-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
                                                            💰 {s.cost_per_person_min === 0 && s.cost_per_person_max === 0 ? "Gratis" : `${s.cost_per_person_min}–${s.cost_per_person_max} €/pers`}
                                                            {s.cost_note && <span className="text-stone-500 font-normal">· {s.cost_note}</span>}
                                                        </div>
                                                    ) : null}
                                                    {(s.lat && s.lng) && (
                                                        <a href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline mt-1 inline-flex items-center gap-1">
                                                            <MapPin size={10} /> Apri in Maps
                                                        </a>
                                                    )}
                                                    <StopMediaGallery
                                                        stop={s}
                                                        onOpen360={(p) => setPanorama({ url: p.url, caption: p.caption || s.name })}
                                                        onOpenPhoto={(p) => window.open(p.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url, "_blank")}
                                                    />
                                                </div>
                                            </li>
                                        );})}
                                    </ol>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <Panorama360Viewer
                open={!!panorama}
                url={panorama?.url}
                caption={panorama?.caption}
                onClose={() => setPanorama(null)}
            />
        </div>
    );
}
